import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(form);
      toast.success(`Welcome, ${user.name}`);
      nav("/patient/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block bg-emerald-600 text-white p-12 relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-white text-emerald-700 grid place-items-center font-bold">D</div>
          <span className="text-xl font-bold">DocCare</span>
        </Link>
        <div className="mt-24 max-w-md">
          <h1 className="text-4xl font-bold leading-tight">Create your patient account.</h1>
          <p className="mt-4 text-emerald-50">Book your first appointment in under a minute.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center font-bold">D</div>
            <span className="text-xl font-bold text-slate-900">DocCare</span>
          </Link>

          <h2 className="text-3xl font-bold text-slate-900">Create account</h2>
          <p className="text-slate-500 mt-2">Only patients can register here. Doctors are onboarded by admins.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} className="mt-1.5 rounded-xl h-11" data-testid="reg-name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 rounded-xl h-11" data-testid="reg-email" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-xl h-11" data-testid="reg-phone" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5 rounded-xl h-11" data-testid="reg-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-xl h-11" data-testid="reg-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-slate-900 underline" data-testid="link-login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
